import { ClientsClient, ConversionDataType, GetClient, GetConversionInfo } from '../../generated-client/generated-client';
import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { Subject, Subscription, of } from 'rxjs';
import { delay, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { ConversionInfoDetailComponent } from '../conversion-info-detail/conversion-info-detail.component';
import { ConversionInfosService } from '../../services/conversion-infos.service';
import { DataSource } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MultiSelectClientComponent } from '../multi-select-client/multi-select-client.component';
import { PaginationResult } from 'ng-lightquery';

@Component({
  selector: 'da-conversion-infos',
  templateUrl: './conversion-infos.component.html',
  styleUrls: ['./conversion-infos.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    MatSelect,
    MatOption,
    MultiSelectClientComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DatePipe
  ]
})
export class ConversionInfosComponent implements OnInit, OnDestroy {
  conversionInfosService = inject(ConversionInfosService);
  private clientsClient = inject(ClientsClient);
  private dialog = inject(MatDialog);

  private conversionInfosPaginatedSubscription: Subscription;
  conversionInfosPaginated = signal<PaginationResult<GetConversionInfo>>(null);
  dataSource: DataSource<GetConversionInfo>;
  private fileNameFilterSource = new Subject<string>();
  fileNameFilter = signal<string>(null);
  clients = signal<GetClient[]>([]);
  conversionDataTypeList = Object.keys(ConversionDataType);
  selectedSourceType = signal<ConversionDataType | null>(null);
  selectedDestinationType = signal<ConversionDataType | null>(null);

  ngOnInit() {
    this.dataSource = this.conversionInfosService;
    this.fileNameFilter.set(this.conversionInfosService.getQueryParameter('fileNameFilter'));
    this.fileNameFilterSource
      .pipe(
        switchMap(x => of<string>(x).pipe(delay(250))),
        distinctUntilChanged()
      )
      .subscribe((filterValue: string) => {
        this.conversionInfosService.setQueryParameter('fileNameFilter', filterValue);
      });
    this.conversionInfosPaginatedSubscription = this.conversionInfosService.paginationResult.subscribe(
      r => this.conversionInfosPaginated.set(r)
    );
    this.clientsClient.getClients(null, null).subscribe(c => this.clients.set(c));
    this.filterClients([]);
    this.excludedClients([]);
  }

  ngOnDestroy() {
    this.conversionInfosPaginatedSubscription.unsubscribe();
    this.filterClients([]);
    this.excludedClients([]);
    this.conversionInfosService.removeQueryParameter('clientId');
    this.conversionInfosService.removeQueryParameter('excludedClientId');
    this.conversionInfosService.removeQueryParameter('sourceType');
    this.conversionInfosService.removeQueryParameter('destinationType');
  }

  onFileNameFilter(fileNameFilter: string) {
    this.fileNameFilter.set(fileNameFilter);
    this.fileNameFilterSource.next(fileNameFilter);
  }

  onPage(pageEvent: PageEvent) {
    this.conversionInfosService.page = pageEvent.pageIndex + 1;
    this.conversionInfosService.pageSize = pageEvent.pageSize;
  }

  onSort(event: { active: string; direction: string }) {
    if (!event.direction) {
      this.conversionInfosService.sort = null;
    } else {
      this.conversionInfosService.sort = { propertyName: event.active, isDescending: event.direction === 'desc' };
    }
  }

  showDetail(conversionInfo: GetConversionInfo) {
    this.dialog.open(ConversionInfoDetailComponent, {
      data: conversionInfo
    });
  }

  filterClients(clientList: GetClient[]): void {
    this.conversionInfosService.setQueryParameter('clientId', clientList.length ? clientList.map(item => item.clientId).join() : null);
  }

  excludedClients(clientList: GetClient[]): void {
    this.conversionInfosService.setQueryParameter(
      'excludedClientId',
      clientList.length ? clientList.map(item => item.clientId).join() : null
    );
  }

  changeSourceType(value: string): void {
    if (value) {
      console.log('SETTING');
      this.conversionInfosService.setQueryParameter('sourceType', value);
    } else {
      this.conversionInfosService.removeQueryParameter('sourceType');
    }
  }

  changeDestinationType(value: string): void {
    if (value) {
      this.conversionInfosService.setQueryParameter('destinationType', value);
    } else {
      this.conversionInfosService.removeQueryParameter('destinationType');
    }
  }
}
